import { formatDistanceToNow } from 'date-fns'
import { formatAsOf } from '../format.js'
import { cx } from './cx.js'
import styles from './AsOfLine.module.css'

const STALE_AFTER_MS = 5 * 60_000

export interface AsOfLineProps {
  /** ISO timestamp of when the data was fetched. */
  asOf: string
  onRefresh: () => void
  /** Injectable for tests; defaults to the real current time. */
  now?: Date
}

/**
 * UI spec §2's as-of line, with the staleness rule from addendum §E: past
 * five minutes it switches to relative time and the muted-amber treatment —
 * the honesty principle applied to the honesty widget itself. Purely
 * presentational: re-rendering on an interval so the relative time keeps
 * advancing is the calling surface's job, not this component's.
 */
export function AsOfLine({ asOf, onRefresh, now = new Date() }: AsOfLineProps) {
  const asOfDate = new Date(asOf)
  const ageMs = now.getTime() - asOfDate.getTime()
  const stale = ageMs > STALE_AFTER_MS

  const label = stale
    ? `as of ${formatDistanceToNow(asOfDate, { addSuffix: true })}`
    : // `formatInstant`, not `format(…, "'UTC'")` — the latter rendered the
      // BROWSER's timezone and appended a UTC label that was false everywhere
      // except UTC. `LGH-024`.
      /**
       * `formatAsOf`, with no tenant timezone — `LGH-025`.
       *
       * **This renders identically today** (`formatAsOf` falls back to the
       * explicit UTC instant when no calendar is declared) and it is the seam
       * rather than a rewrite: when `PLT-046` serves the tenant's timezone,
       * this one call gains `{ tenantTimeZone }` and the founder's same-day
       * rule starts applying — in the business's calendar, never the reader's.
       *
       * Deliberately NOT threading a prop through every call site or standing
       * up a context for it now: the field does not exist, and guessing how it
       * will arrive is how a shape gets built twice.
       */
      `as of ${formatAsOf(asOf)}`

  return (
    <div className={cx(styles['line'], stale && styles['stale'])}>
      <span>{label}</span>
      <button type="button" className={styles['refresh']} onClick={onRefresh}>
        ↻ Refresh
      </button>
    </div>
  )
}
